import { type FC, useState } from "react";

export interface Notification {
  id: string;
  title: string;
  message: string;
  createdAt: string | Date | { toDate: () => Date };
  isRead: boolean;
}

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: Notification[];
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
}

function formatTimestamp(ts: Notification["createdAt"]): string {
  if (!ts) return "";
  let date: Date;
  if (ts instanceof Date) {
    date = ts;
  } else if (typeof (ts as any).toDate === "function") {
    date = (ts as any).toDate();
  } else {
    date = new Date(ts as any);
  }
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-KE", { day: "numeric", month: "short" });
}

export const NotificationPanel: FC<NotificationPanelProps> = ({
  isOpen,
  onClose,
  notifications,
  onMarkRead,
  onMarkAllRead,
}) => {
  const [filter, setFilter] = useState<"unread" | "all">("unread");

  const unread = notifications.filter((n) => !n.isRead);
  const displayed = filter === "unread" ? unread : notifications;
  const hasUnread = unread.length > 0;

  return (
    <>
      {/* Backdrop — closes panel on click */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20"
          aria-hidden="true"
          onClick={onClose}
        />
      )}

      {/* Slide-out panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Notifications"
        className={[
          "fixed inset-y-0 right-0 z-40 flex w-80 flex-col bg-white shadow-xl transition-transform duration-300 ease-in-out sm:w-96",
          isOpen ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
      >
        {/* Header */}
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-gray-200 px-4">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900">
              Notifications
            </h2>
            {hasUnread && (
              <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">
                {unread.length > 99 ? "99+" : unread.length}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {hasUnread && (
              <button
                type="button"
                className="text-xs font-medium text-primary-600 hover:text-primary-800 focus:outline-none focus:underline"
                onClick={onMarkAllRead}
              >
                Mark all as read
              </button>
            )}
            <button
              type="button"
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
              onClick={onClose}
              aria-label="Close notifications"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Tab Selection */}
        <div className="flex border-b border-gray-100 bg-gray-50/50">
          <button
            type="button"
            onClick={() => setFilter("unread")}
            className={[
              "flex-1 py-2.5 text-center text-xs font-semibold border-b-2 transition-colors focus:outline-none",
              filter === "unread"
                ? "border-primary-600 text-primary-600 bg-white"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100/50",
            ].join(" ")}
          >
            Unread ({unread.length})
          </button>
          <button
            type="button"
            onClick={() => setFilter("all")}
            className={[
              "flex-1 py-2.5 text-center text-xs font-semibold border-b-2 transition-colors focus:outline-none",
              filter === "all"
                ? "border-primary-600 text-primary-600 bg-white"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100/50",
            ].join(" ")}
          >
            All Recent ({notifications.length})
          </button>
        </div>

        {/* Notification list */}
        <div className="flex-1 overflow-y-auto">
          {displayed.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
              <svg
                className="h-12 w-12 text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.25}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
                />
              </svg>
              <p className="text-sm font-medium text-gray-500">
                {filter === "unread" ? "You're all caught up" : "No notifications yet"}
              </p>
              <p className="text-xs text-gray-400">
                {filter === "unread"
                  ? "No unread notifications right now."
                  : "No notifications generated for your account."}
              </p>
            </div>
          ) : (
            <ul role="list" className="divide-y divide-gray-100">
              {displayed.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    className="w-full px-4 py-4 text-left transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                    onClick={() => !notification.isRead && onMarkRead(notification.id)}
                    disabled={notification.isRead}
                    aria-label={notification.isRead ? notification.title : `Mark "${notification.title}" as read`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Unread dot indicator */}
                      {!notification.isRead ? (
                        <span
                          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary-500 animate-pulse"
                          aria-hidden="true"
                        />
                      ) : (
                        <span
                          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-transparent"
                          aria-hidden="true"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className={["text-sm font-semibold", !notification.isRead ? "text-gray-900" : "text-gray-400"].join(" ")}>
                          {notification.title}
                        </p>
                        <p className={["mt-0.5 text-sm line-clamp-2", !notification.isRead ? "text-gray-600" : "text-gray-400"].join(" ")}>
                          {notification.message}
                        </p>
                        <p className="mt-1 text-xs text-gray-400">
                          {formatTimestamp(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
};
